import { Box, ButtonProps, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SendIcon from "@mui/icons-material/Send";
import VerifiedIcon from "@mui/icons-material/Verified";
import { GridDeleteIcon } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import ButtonsRow from "../../components/common/ButtonsRow";
import { ICustomButtonProps } from "../../components/common/CustomButton";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomMonthYearPicker from "../../components/common/CustomMonthYearPicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import SendCustomerBillEmailDialog from "../../components/SendCustomerBillEmailDialog";
import config from "../../config.json";
import { fetchAccounts } from "../../services/AccountService";
import {
  fetchCurrency,
  getCurrencyOptions,
  useCurrenciesQuery,
} from "../../services/CurrencyService";
import {
  BulkGenerateRequest,
  ClientInvoiceRate,
  confirmCustomerBill,
  createCustomerBill,
  CustomerBillType,
  deleteCustomerBills,
  exportCustomerBill,
  fetchAccountsByProfile,
  fetchCustomerBillHistory,
  pdfApi,
  pdfCustomerDetailsApi,
  sendEmails,
  sendSingleEmail,
  useCustomerBillQuery,
} from "../../services/CustomerBillService";
import {
  fetchProfile,
  useProfilesDropdown,
  useProfilesQuery,
} from "../../services/ProfilesService";
import { DateRange, IField, Params } from "../../types/table/interfaces.type";
import { pageAsOptions } from "../../utils/commonDataUtils";
import ToastNotification from "../../components/ToastNotification";
import { ReactComponent as Confirm } from "../../assets/icons/confirm.svg";
import { ReactComponent as Export } from "../../assets/icons/export.svg";

import History from "../../components/History";
import GoldMultiSelectField, {
  MONTH_OPTIONS,
} from "../../components/GoldMultiSelectField";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface Option {
  label: string;
  value: string;
}

interface ConfirmDialogContentProps {
  invoiceRef: string;
  amount: number;
  profileId: string;
}

const ConfirmDialogContent: React.FC<ConfirmDialogContentProps> = ({
  invoiceRef,
  amount,
  profileId,
}) => {
  const { data: currency } = useQuery(getCurrencyOptions(profileId));

  const amountDisplay =
    amount !== undefined && amount !== null ? Number(amount).toFixed(3) : "-";

  const currencyDisplay = currency ? ` ${currency}` : "";

  return (
    <Box>
      <Typography>
        Are you sure you want to confirm the invoice of ref{" "}
        <strong>{invoiceRef}</strong> and amount{" "}
        <strong>
          {amountDisplay}
          {currencyDisplay}
        </strong>
        ?
      </Typography>
    </Box>
  );
};

const CustomerBillPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [filters, setFilters] = useState<Params<CustomerBillType>>({});
  const [filterDeleted, setFilterDeleted] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<
    Params<CustomerBillType & DateRange & { deleted?: boolean }>
  >({
    search: "",
    profileId: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [trafficDate, setTrafficDate] = useState<Date | null>(null);
  const [profileOptions, setProfileOptions] = useState<Option[]>([]);
  const [accountOptions, setAccountOptions] = useState<Option[]>([]);
  const [currencyMap, setCurrencyMap] = useState<Record<number, string>>({});

  const [profileMap, setProfileMap] = useState<
    Record<
      string,
      { name: string; accountManagerEmail: string; currencyId: string }
    >
  >({});

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmingInvoice, setConfirmingInvoice] = useState<{
    id: string;
    ref: string;
    amount: number;
    profileId: string;
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const canConfirmInvoice =
    !isConfirming && hasJwtPermission("CONFIRM_CLIENT_INVOICE");

  const isConfirmActionDisabled = (row: any) =>
    row?.status === "CONFIRMED" ||
    row?.status === "SENT" ||
    !hasJwtPermission("CONFIRM_CLIENT_INVOICE");

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const { data: dataProfile } = useProfilesQuery();
  const { data: dataProfiledropdown = [] } = useProfilesDropdown(filterDeleted);
  const { data: currencyOption } = useCurrenciesQuery();

  useEffect(() => {
    if (dataProfile) {
      setProfileOptions(dataProfile.profileOptions);
      setProfileMap(dataProfile.profileMap);
    }
    if (currencyOption) {
      const map = currencyOption.reduce<Record<number, string>>((acc, curr) => {
        acc[curr.id] = curr.name;
        return acc;
      }, {});
      setCurrencyMap(map);
    }
  }, [currencyOption, queryClient, dataProfile]);

  const dialogRef = useRef<CustomerBillDialogRef>(null);

  const handleOpenDialog = (mode: "add" | "send" | "history", id?: string) => {
    if (mode === "send") {
      dialogRef.current?.openSendDialog();
    } else {
      dialogRef.current?.openDialog(mode, id);
    }
  };
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const accountsPage = await fetchAccounts({
          sort: "name",
          size: config.MAX_PAGE_SIZE,
        });
        const accountsAsOptions = pageAsOptions(accountsPage).map((option) => ({
          ...option,
          value: String(option.value),
        }));
        setAccountOptions(accountsAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      startDate: trafficDate ?? undefined,
      profileId: filters.profileId,
      excludeZero: filters.excludeZero,
      status: filters.status,
      deleted: filterDeleted,
    }));
    setSelectedRowIds([]);
  };

  const handleOpenConfirmDialog = (
    id: string,
    ref: string,
    amount: number,
    profileId: string,
  ) => {
    setConfirmingInvoice({ id, ref, amount, profileId });
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setConfirmingInvoice(null);
  };

  const handleConfirmInvoice = async () => {
    if (!confirmingInvoice) return;

    setIsConfirming(true);
    try {
      await confirmCustomerBill(confirmingInvoice.id);
      ToastNotification.success("Customer invoice confirmed successfully!");
      handleCloseConfirmDialog();
      queryClient.invalidateQueries();
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to confirm invoice.");
    } finally {
      setIsConfirming(false);
    }
  };

  const onRowSelect = (row: CustomerBillType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      return newSelected;
    });
  };

  const isRowSelected = (row: CustomerBillType) =>
    selectedRowIds.includes(String(row.id));

  const handleOpenDeleteDialog = () => {
    if (selectedRowIds.length === 0) {
      ToastNotification.warning(
        "Please select at least one invoice to delete.",
      );
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteInvoices = async () => {
    setIsDeleting(true);
    try {
      await deleteCustomerBills(selectedRowIds);
      ToastNotification.success("Customer invoices deleted successfully!");
      handleCloseDeleteDialog();
      setSelectedRowIds([]);
      queryClient.invalidateQueries();
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to delete invoices.");
    } finally {
      setIsDeleting(false);
    }
  };

  const actions = Actions({
    onPdf: {
      handler: async (id: string | number) => {
        await pdfApi(String(id));
      },
      isDisabled: !hasJwtPermission("GET_CLIENT_INVOICE_PDF"),
    },
    onDetails: {
      handler: async (id: string | number) => {
        await pdfCustomerDetailsApi(String(id));
      },
      isDisabled: !hasJwtPermission("GET_CLIENT_INVOICE_DETAILS_PDF"),
    },
    onViewHistory: {
      handler: (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_CLIENT_INVOICE_HISTORY"),
    },
    onConfirm: {
      handler: (id: string | number, row?: any) => {
        const ref = row?.ref || `#${id}`;
        const amount = row?.amount || 0;
        const profileId = Array.isArray(row?.profileId)
          ? row.profileId[0]
          : row?.profileId || "";
        handleOpenConfirmDialog(String(id), ref, amount, profileId);
      },
      isDisabled: isConfirmActionDisabled,
      icon: (row: any) => {
        const isDisabled = isConfirmActionDisabled(row);
        return (
          <Tooltip title="Confirm">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: isDisabled ? "transparent" : `${COLORS.successGreen}18`,
                border: isDisabled
                  ? "none"
                  : `1.5px solid ${COLORS.successGreen}55`,
              }}
            >
              <Confirm
                stroke={isDisabled ? "#D3D3D3" : COLORS.successGreen}
                width="18"
                height="22"
              />
            </Box>
          </Tooltip>
        );
      },
    },
  });

  const autoCompletes = [
    {
      label: "Account Profile",
      value: filters.profileId || "",
      options: dataProfiledropdown,
      isDeleted: filterDeleted,
      clearable: true,
      onToggleDeleted: () => setFilterDeleted((prev) => !prev),
      placeholder: "Select Profile",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProfileOption = dataProfiledropdown.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          profileId: String(selectedProfileOption?.value || ""),
        }));
      },
    },
  ];

  const dropdowns = [
    {
      label: "Exclude Zero",
      value:
        filters.excludeZero === undefined ? "" : String(filters.excludeZero),
      options: [
        { label: "Exclude Zero", value: "true" },
        { label: "Don't Exclude Zero", value: "false" },
      ],
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,
          excludeZero:
            value === "true" ? true : value === "false" ? false : undefined,
        }));
      },
    },
    {
      label: "Status",
      value: filters.status || "",
      options: [
        { label: "Generated", value: "GENERATED" },
        { label: "Sent", value: "SENT" },
        { label: "Confirmed", value: "CONFIRMED" },
        { label: "Pending", value: "PENDING" },
        { label: "Error", value: "ERROR" },
      ],
      placeholder: "unselect",
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,
          status: value ? (String(value) as any) : undefined,
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_CLIENTS_INVOICES",
    },
  ];

  const form2Buttons: ICustomButtonProps[] = [
    {
      label: "Generate invoice",
      size: isSmallScreen ? "medium" : "large",
      variant: "outlined",
      textColor: COLORS.GOLD,
      borderColor: COLORS.GOLD,
      borderRadius: "8px",
      bold: "bold",
      onClick: () => handleOpenDialog("add"),
      permission: "BULK_GENERATE_CLIENT_INVOICE",
    },
    {
      label: "Send Email",
      size: isSmallScreen ? "medium" : "large",
      variant: "contained",
      bgColor: COLORS.GOLD,
      borderColor: COLORS.GOLD,
      borderRadius: "8px",
      bold: "bold",
      onClick: () => handleOpenDialog("send"),
      permission: "SEND_CLIENT_INVOICE",
    },
  ];

  const fields = useMemo<IField<CustomerBillType>[]>(
    () => [
      {
        key: "profileId",
        header: "Profile",
        transform: async (t) => {
          if (!t.profileId || t.profileId.length === 0) return "Unknown";

          if (Array.isArray(t.profileId)) {
            const profileNames = t.profileId
              .map((id) => profileMap[id]?.name || "Unknown")
              .join(", ");
            return profileNames || "Unknown";
          }
          const profile = profileMap[t.profileId];
          return profile?.name || "Unknown";
        },
      },
      { key: "ref", header: "Invoice Name" },
      {
        key: "status",
        header: "Status",
        transform: (row) => {
          const status = String(row.status)?.toUpperCase();
          switch (status) {
            case "GENERATED":
              return (
                <Tooltip title="Generated">
                  <CheckCircleIcon sx={{ color: "green" }} />
                </Tooltip>
              );
            case "SENT":
              return (
                <Tooltip title="Sent">
                  <SendIcon sx={{ color: "#1976d2" }} />
                </Tooltip>
              );
            case "CONFIRMED":
              return (
                <Tooltip title="Confirmed">
                  <VerifiedIcon sx={{ color: "#1976d2" }} />
                </Tooltip>
              );
            case "PENDING":
              return (
                <Tooltip title="Pending">
                  <HourglassEmptyIcon sx={{ color: "orange" }} />
                </Tooltip>
              );
            case "ERROR":
              return (
                <Tooltip title="Error">
                  <CancelIcon sx={{ color: "red" }} />
                </Tooltip>
              );
            default:
              return status || "-";
          }
        },
      },
      {
        key: "createdDate",
        header: "Created Date",
        transform: (t) =>
          t.createdDate
            ? new Date(t.createdDate).toLocaleString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZone: "UTC",
              })
            : "-",
      },
      {
        key: "startDate",
        header: "Invoice Month",
        transform: (t) => {
          if (!t.startDate) return "-";

          const formatFullDate = (date: Date | string) =>
            new Date(date).toLocaleString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              timeZone: "UTC",
            });

          const monthYear = new Date(t.startDate).toLocaleString("en-GB", {
            month: "short",
            year: "numeric",
            timeZone: "UTC",
          });

          return (
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2">
                    Start Date: {formatFullDate(t.startDate)}
                  </Typography>
                  <Typography variant="body2">
                    End Date: {t.endDate ? formatFullDate(t.endDate) : "-"}
                  </Typography>
                </Box>
              }
            >
              <span>{monthYear}</span>
            </Tooltip>
          );
        },
      },
      {
        key: "amount",
        header: "System Amount",
        transform: (t) =>
          t.amount !== undefined && t.amount !== null
            ? Number(t.amount).toFixed(3)
            : "-",
      },
      {
        key: "id",
        header: "Currency",
        transform: async (row) => {
          const profileId = Array.isArray(row.profileId)
            ? row.profileId[0]
            : row.profileId;
          if (!profileId) return "-";
          try {
            const profile = await queryClient.fetchQuery({
              queryKey: ["profile", profileId],
              queryFn: () => fetchProfile(String(profileId)),
            });
            if (!profile?.currencyId) return "-";
            const currency = await queryClient.fetchQuery({
              queryKey: ["currency", profile.currencyId],
              queryFn: () => fetchCurrency(Number(profile.currencyId)),
            });
            return currency?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
    ],
    [queryClient, profileMap],
  );
  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Client Invoice"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <Box display="flex" flexWrap="wrap" gap={2}>
              <CustomDateRangePicker
                label="Creation Date Range"
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
              />
              <CustomMonthYearPicker
                label="Invoice Month"
                value={trafficDate}
                onChange={setTrafficDate}
              />
            </Box>
          }
          autoCompletes={autoCompletes}
          dropdowns={dropdowns}
          buttons={buttons}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <ButtonsRow buttons={form2Buttons} />
      </Box>
      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Client Invoices List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: handleOpenDeleteDialog,
              textColor: COLORS.iconColor,
              permission: "DELETE_CLIENT_INVOICE",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportCustomerBill(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_CLIENT_INVOICES_CSV",
            },
          ]}
        />
        <CustomTable
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useCustomerBillQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={[
            "ref",
            "createdDate",
            "startDate",
            "endDate",
            "amount",
          ]}
        />
      </Box>
      <CustomerBillDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        Accounts={accountOptions}
        Profiles={profileOptions}
        appliedFilters={appliedFilters}
        profileMap={profileMap}
      />

      {/* Confirmation Dialog */}
      <CustomDialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        mode="confirm"
        title="Confirm Invoice"
        disableRestoreFocus
        fields={[
          {
            type: "custom",
            component: confirmingInvoice ? (
              <ConfirmDialogContent
                invoiceRef={confirmingInvoice.ref}
                amount={confirmingInvoice.amount}
                profileId={confirmingInvoice.profileId}
              />
            ) : null,
          },
        ]}
        buttons={[
          {
            label: isConfirming ? "Confirming..." : "Confirm",
            variant: "contained",
            bgColor: canConfirmInvoice ? COLORS.successGreen : COLORS.GOLD,
            textColor: COLORS.White,
            bold: canConfirmInvoice ? "bold" : undefined,
            borderRadius: "12px",
            onClick: handleConfirmInvoice,
            disabled: !canConfirmInvoice,
            autoFocus: true,
          },
        ]}
      />

      {/* Delete Dialog */}
      <CustomDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        mode="delete"
        title="Delete Invoices"
        fields={[
          {
            type: "custom",
            component: (
              <Box>
                <Typography>
                  Are you sure you want to delete {selectedRowIds.length}{" "}
                  selected invoice(s)?
                </Typography>
              </Box>
            ),
          },
        ]}
        buttons={[
          {
            label: isDeleting ? "Deleting..." : "Delete",
            variant: "contained",
            bgColor: COLORS.GOLD,
            textColor: COLORS.iconColor,
            borderRadius: "12px",
            onClick: handleDeleteInvoices,
            disabled: isDeleting || !hasJwtPermission("DELETE_CLIENT_INVOICE"),
          },
        ]}
      />
    </Box>
  );
};

export interface CustomerBillDialogRef {
  openDialog: (mode: "add" | "history", id?: string) => Promise<void>;
  openSendDialog: () => void;
}

interface CustomerBillDialogProps {
  onRefresh: () => void;
  Accounts: Option[];
  Profiles: Option[];
  profileMap: Record<string, { name: string }>;
  appliedFilters: Params<CustomerBillType>;
}

const CustomerBillDialog = forwardRef<
  CustomerBillDialogRef,
  CustomerBillDialogProps
>(({ onRefresh, Profiles, profileMap, appliedFilters }, ref) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "send" | "history">(
    "add",
  );
  const [dialogData, setDialogData] = useState<
    Partial<
      CustomerBillType &
        ClientInvoiceRate & {
          months?: number[];
          year?: number;
          clientConnectionProfileId?: number[];
        }
    >
  >({});

  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [accountsByProfile, setAccountsByProfile] = useState<Option[]>([]);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [attachCcEmail, setAttachCcEmail] = useState<boolean>(true);
  const [selectedId, setSelectedId] = useState<string | number>("");

  const profileSelectOptions = useMemo(
    () =>
      Profiles.map((profile) => ({
        label: profile.label,
        value: String(profile.value),
      })),
    [Profiles],
  );

  const selectedProfileOptions = useMemo(() => {
    const selectedProfileIds = Array.isArray(dialogData.profileId)
      ? dialogData.profileId.map(String)
      : dialogData.profileId
        ? [String(dialogData.profileId)]
        : [];

    return profileSelectOptions.filter((option) =>
      selectedProfileIds.includes(String(option.value)),
    );
  }, [profileSelectOptions, dialogData.profileId]);

  const selectedAccountOptions = useMemo(
    () =>
      accountsByProfile.filter((option) =>
        (dialogData.clientConnectionProfileId || [])
          .map(String)
          .includes(String(option.value)),
      ),
    [accountsByProfile, dialogData.clientConnectionProfileId],
  );

  const selectedMonthOptions = useMemo(
    () =>
      MONTH_OPTIONS.filter((option) =>
        (dialogData.months || []).map(String).includes(String(option.value)),
      ),
    [dialogData.months],
  );

  useImperativeHandle(ref, () => ({
    openDialog: async (mode: "add" | "history", id?: string) => {
      setDialogMode(mode);
      if (mode === "add") {
        setDialogData({
          months: [new Date().getMonth() + 1],
          year: new Date().getFullYear(),
          clientConnectionProfileId: [],
          profileId: [],
        });
      }

      if (mode === "history") {
        setSelectedId(id || "");
      }

      setErrors({});
      setDialogOpen(true);
      setIsSaveDisabled(true);
    },
    openSendDialog: () => {
      setDialogMode("send");
      setSendDialogOpen(true);
    },
  }));

  const validateField = (field: string, value: string) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: !value,
    }));
  };

  useEffect(() => {
    console.log("Dialog Data:", dialogData);

    const isAnyFieldEmpty =
      !(Array.isArray(dialogData.months) && dialogData.months.length > 0) ||
      !dialogData.year ||
      !(
        Array.isArray(dialogData.profileId) && dialogData.profileId.length > 0
      ) ||
      !(
        Array.isArray(dialogData.clientConnectionProfileId) &&
        dialogData.clientConnectionProfileId.length > 0
      );

    console.log("Is any field empty:", isAnyFieldEmpty);

    setIsSaveDisabled(isAnyFieldEmpty);
  }, [dialogData]);

  const closeDialog = () => {
    setDialogOpen(false);
    setSendDialogOpen(false);
  };

  const handleSave = async () => {
    try {
      const clientConnectionProfileIds = Array.isArray(
        dialogData.clientConnectionProfileId,
      )
        ? dialogData.clientConnectionProfileId!.filter(
            (id: number): id is number => typeof id === "number",
          )
        : dialogData.clientConnectionProfileId !== undefined
          ? [dialogData.clientConnectionProfileId]
          : [];

      const periods = (dialogData.months || []).map((month) => ({
        month,
        year: dialogData.year ?? new Date().getFullYear(),
      }));

      const requestData: BulkGenerateRequest = {
        connectionProfileIds: clientConnectionProfileIds,
        periods,
      };

      console.log("Request Data:", requestData);

      const response = await createCustomerBill(requestData);

      if (response.failureCount > 0) {
        const failedPeriods = response.results
          .filter((r) => !r.success)
          .map(
            (r) =>
              `${r.month}/${r.year}${r.errorMessage ? `: ${r.errorMessage}` : ""}`,
          )
          .join(", ");
        ToastNotification.error(failedPeriods);
      }

      onRefresh();
      closeDialog();
      setDialogData({});
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  useEffect(() => {
    const isAnyFieldEmpty =
      !Array.isArray(dialogData.months) ||
      dialogData.months.length === 0 ||
      !dialogData.year ||
      !Array.isArray(dialogData.profileId) ||
      dialogData.profileId.length === 0 ||
      !Array.isArray(dialogData.clientConnectionProfileId) ||
      dialogData.clientConnectionProfileId.length === 0;

    setIsSaveDisabled(isAnyFieldEmpty);
  }, [dialogData]);

  const fetchDynamicAccountsForProfiles = async (
    profileIds: string[],
  ): Promise<void> => {
    try {
      const accountPromises = profileIds.map((profileId) =>
        fetchAccountsByProfile(profileId),
      );
      const accountResponses = await Promise.all(accountPromises);

      const combinedAccounts = accountResponses
        .flatMap((response) => response.content)
        .map((account: { id: number; name: string }) => ({
          label: account.name,
          value: String(account.id),
        }));

      setAccountsByProfile(combinedAccounts);

      setDialogData((prev) => ({
        ...prev,
        clientConnectionProfileId: combinedAccounts.map((account) =>
          Number(account.value),
        ),
      }));
    } catch (error: any) {
      console.error("Error fetching accounts for selected profiles:", error);
      setAccountsByProfile([]);
      setDialogData((prev) => ({
        ...prev,
        clientConnectionProfileId: [],
      }));
    }
  };

  const sendEmail = async (selectedIds: string[], attachCcEmail: boolean) => {
    try {
      if (selectedIds.length === 1) {
        const id = selectedIds[0];
        await sendSingleEmail(id, attachCcEmail);
        console.log(`Email sent for ID: ${id}`);
      } else if (selectedIds.length > 1) {
        await sendEmails(selectedIds, attachCcEmail);
      }
      ToastNotification.success("Email sent successfully!");
      onRefresh();
      closeDialog();
    } catch (error: any) {
      console.error("Error sending emails:", error);
    }
  };
  const isSendDisabled = selectedIds.length === 0;

  const fieldHistory = useMemo<IField<CustomerBillType>[]>(
    () => [
      {
        key: "profileId",
        header: "Profile",
        transform: (t) => {
          if (
            !t.profileId ||
            (Array.isArray(t.profileId) && t.profileId.length === 0)
          )
            return "Unknown";

          if (Array.isArray(t.profileId)) {
            const profileNames = t.profileId
              .map((id) => profileMap[id]?.name || "Unknown")
              .join(", ");
            return profileNames || "Unknown";
          }
          const profile = profileMap[t.profileId as string];
          return profile?.name || "Unknown";
        },
      },
      { key: "ref", header: "Invoice Name" },
      {
        key: "createdDate",
        header: "Invoice Date",
        transform: (t) =>
          t.createdDate
            ? new Date(t.createdDate).toLocaleString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZone: "UTC",
              })
            : "-",
      },
      {
        key: "startDate",
        header: "Start Date",
        transform: (t) =>
          t.startDate
            ? new Date(t.startDate).toLocaleString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZone: "UTC",
              })
            : "-",
      },
      {
        key: "endDate",
        header: "End Date",
        transform: (t) =>
          t.endDate
            ? new Date(t.endDate).toLocaleString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZone: "UTC",
              })
            : "-",
      },
      {
        key: "amount",
        header: "System Amount",
        transform: (t) =>
          t.amount !== undefined && t.amount !== null
            ? Number(t.amount).toFixed(3)
            : "-",
      },
      {
        key: "createdBy",
        header: "Created By",
      },
      {
        key: "confirmedBy",
        header: "Confirmed By",
      },
      {
        key: "sentBy",
        header: "Sent By",
      },
    ],
    [profileMap],
  );

  return (
    <>
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        mode={dialogMode}
        title={dialogMode === "history" ? "History" : "Generate New Invoice"}
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters as Params<CustomerBillType>}
                      fields={fieldHistory}
                      fetchFunc={({ size = 10 }) =>
                        fetchCustomerBillHistory(Number(selectedId), {
                          ...appliedFilters,
                          size,
                        })
                      }
                    />
                  ),
                },
              ]
            : [
                {
                  type: "custom",
                  fullWidth: true,
                  component: (
                    <GoldMultiSelectField
                      label="Month"
                      placeholder="Select Month(s)"
                      options={MONTH_OPTIONS}
                      value={selectedMonthOptions}
                      onChange={(selected) => {
                        const selectedMonths = selected.map((option) =>
                          Number(option.value),
                        );
                        setDialogData((prev) => ({
                          ...prev,
                          months: selectedMonths,
                        }));
                        validateField("months", selectedMonths.join(","));
                      }}
                      error={!!errors.months}
                      helperText={
                        errors.months
                          ? "At least one month must be selected"
                          : ""
                      }
                    />
                  ),
                },
                {
                  type: "select",
                  label: "Year",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.year || new Date().getFullYear(),
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      year: Number(value),
                    }));
                    validateField("year", value as string);
                  },
                  options: Array.from(
                    { length: new Date().getFullYear() - 2000 + 1 },
                    (_, index) => ({
                      label: (2000 + index).toString(),
                      value: (2000 + index).toString(),
                    }),
                  ),
                  error: !!errors.year,
                  helperText: errors.year ? "Year is required" : "",
                },

                {
                  type: "custom",
                  fullWidth: true,
                  component: (
                    <GoldMultiSelectField
                      label="Profile"
                      placeholder="Select Profile"
                      options={profileSelectOptions}
                      value={selectedProfileOptions}
                      onChange={async (selected) => {
                        const selectedProfiles = selected.map((option) =>
                          String(option.value),
                        );
                        setDialogData((prev) => ({
                          ...prev,
                          profileId: selectedProfiles,
                        }));
                        validateField("profileId", selectedProfiles.join(","));
                        if (selectedProfiles.length > 0) {
                          await fetchDynamicAccountsForProfiles(
                            selectedProfiles,
                          );
                        } else {
                          setAccountsByProfile([]);
                        }
                      }}
                      error={!!errors.profileId}
                      helperText={
                        errors.profileId
                          ? "At least one profile must be selected"
                          : ""
                      }
                    />
                  ),
                },
                {
                  type: "custom",
                  fullWidth: true,
                  component: (
                    <GoldMultiSelectField
                      label="Accounts"
                      placeholder="Select Accounts"
                      options={accountsByProfile}
                      value={selectedAccountOptions}
                      onChange={(selected) => {
                        const stringifiedAccounts = selected.map((option) =>
                          String(option.value),
                        );
                        validateField(
                          "clientConnectionProfileId",
                          stringifiedAccounts.join(","),
                        );
                        setDialogData((prev) => ({
                          ...prev,
                          clientConnectionProfileId:
                            stringifiedAccounts.map(Number),
                        }));
                      }}
                      error={!!errors.clientConnectionProfileId}
                      helperText={
                        errors.clientConnectionProfileId
                          ? "Account is required"
                          : ""
                      }
                    />
                  ),
                },
              ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                {
                  label: "Save",
                  variant: "contained",
                  bgColor: COLORS.GOLD,
                  borderRadius: "12px",
                  onClick: handleSave,
                  disabled:
                    isSaveDisabled ||
                    !hasJwtPermission("BULK_GENERATE_CLIENT_INVOICE"),
                },
              ]
        }
      />
      {dialogMode === "send" && (
        <CustomDialog
          open={sendDialogOpen}
          onClose={closeDialog}
          mode={dialogMode}
          title="Send Email"
          fields={[
            {
              type: "custom",
              component: (
                <SendCustomerBillEmailDialog
                  onSelectedIdsChange={(ids) => setSelectedIds(ids)}
                  onAttachCcEmailChange={(value) => setAttachCcEmail(value)}
                />
              ),
            },
          ]}
          buttons={[
            {
              label: selectedIds.length ? `Send (${selectedIds.length})` : "Send",
              variant: "contained",
              bgColor: COLORS.GOLD,
              borderRadius: "12px",
              onClick: () => sendEmail(selectedIds, attachCcEmail),
              disabled:
                isSendDisabled || !hasJwtPermission("SEND_CLIENT_INVOICE"),
            },
          ]}
        />
      )}
    </>
  );
});

export default CustomerBillPage;
